import dayjs from 'dayjs';
import { gql, useQuery } from '@apollo/client';
import { Paper, Link, Box, Breadcrumbs, Typography, SxProps, Skeleton } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ServiceOrderDetail } from 'types/serviceOrder.types';
import TimelineIcon from 'components/TimelineIcon';
import { TimelineTypeEnum } from 'types/timeline.types';
import ImagesBlock from 'components/ImagesBlock';
import { Account } from 'types/Account';
import { useRecoilValue } from 'recoil';
import { accountSelectedSelector } from 'atoms';

const SERVICE_ORDER_DETAILS = gql`
  query ServiceOrderDetails(
    $isCloud: Boolean!
    $masterCompanyId: Int!
    $accountId: Int!
    $id: Int!
    $timezone: Int
    $webService: String
    $userId: Int
    $email: String
  ) {
    getServiceOrderDetails(
      isCloud: $isCloud
      masterCompanyId: $masterCompanyId
      accountId: $accountId
      id: $id
      timezone: $timezone
      webService: $webService
      userId: $userId
      email: $email
    ) {
      id
      serviceOrderId
      sequential
      type
      date
      endDate
      hasOSImages
      defect
      description
      observation
      scheduleDate
      executant
      solution
      cause
      status
      images {
        date
        url
      }
    }
  }
`;

function ServiceOrderDetails({
  serviceOrderId,
  account,
}: {
  serviceOrderId: number;
  account: Account;
}) {
  const { t } = useTranslation(['_common', 'service_order']);

  const { data, error, loading } = useQuery<{
    getServiceOrderDetails: ServiceOrderDetail;
  }>(SERVICE_ORDER_DETAILS, {
    variables: {
      ...account,
      accountId: account.id,
      id: Number(serviceOrderId),
    },
  });

  if (error) {
    throw error;
  }

  return (
    <Box
      sx={{
        height: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link component={RouterLink} underline="hover" color="inherit" to={'/service-orders'}>
          {t('_common:service_orders')}
        </Link>
        <Typography color="text.primary">{t('service_order:service_order_details')}</Typography>
      </Breadcrumbs>

      <ServiceOrderDetailsSummary
        serviceOrder={data?.getServiceOrderDetails}
        {...{ loading, account }}
      />
      {data?.getServiceOrderDetails?.images && data.getServiceOrderDetails.images.length > 0 && (
        <ImagesBlock images={data.getServiceOrderDetails.images} />
      )}
    </Box>
  );
}

function ServiceOrderDetailsSummary({
  serviceOrder,
  loading,
  account,
}: {
  serviceOrder?: ServiceOrderDetail;
  loading: boolean;
  account: Account;
}) {
  const { t } = useTranslation(['service_order', '_common']);
  const accountSelected = useRecoilValue(accountSelectedSelector);
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, flex: '0 0 auto' }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6">{t('service_order:service_order_details')}</Typography>
        {!accountSelected && (
          <>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'inline-block' }}>
              {t('_common:account')}:
            </Typography>
            <Typography variant="body2" sx={{ ml: 0.5, display: 'inline-block' }}>
              {account.accountCode} - {account.displayName}
            </Typography>
          </>
        )}
      </Box>
      <Box sx={{ display: 'flex', mb: 2 }}>
        <span>
          <TimelineIcon type={TimelineTypeEnum.SERVICE_ORDER} />
        </span>
        <ServiceOrderDetailsSummaryItem
          label={t('_common:service_order')}
          value={serviceOrder ? serviceOrder?.defect : <Skeleton variant="text" />}
          sx={{ flex: '1 1 auto' }}
        />
        <ServiceOrderDetailsSummaryItem
          label={t('_common:state')}
          value={
            serviceOrder ? (
              /* @ts-ignore */
              t(`service_order:${String(serviceOrder.status)}`)
            ) : (
              <Skeleton variant="text" />
            )
          }
        />
        <ServiceOrderDetailsSummaryItem
          label={t('service_order:opening_date')}
          value={
            serviceOrder ? dayjs(serviceOrder.date).format('L LTS') : <Skeleton variant="text" />
          }
        />
        <ServiceOrderDetailsSummaryItem
          label={t('service_order:closing_date')}
          value={
            serviceOrder ? (
              serviceOrder.endDate ? (
                dayjs(serviceOrder.endDate).format('L LTS')
              ) : (
                ''
              )
            ) : (
              <Skeleton variant="text" />
            )
          }
        />
      </Box>
      <Box sx={{ display: 'flex' }}>
        <ServiceOrderDetailsSummaryItem
          label={t('_common:description')}
          value={loading ? <Skeleton variant="text" /> : serviceOrder?.description}
          sx={{ flex: '1 1 auto' }}
        />
        <ServiceOrderDetailsSummaryItem
          label={t('service_order:technician')}
          value={loading ? <Skeleton variant="text" /> : serviceOrder?.executant}
        />
      </Box>
    </Paper>
  );
}

function ServiceOrderDetailsSummaryItem({
  label,
  value,
  sx,
}: {
  label: string;
  value?: string | JSX.Element | null;
  sx?: SxProps;
}) {
  if (!value) {
    return null;
  }

  return (
    <Box sx={{ mr: 2, ...sx }}>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {label}
      </Typography>
      <Typography>{value}</Typography>
    </Box>
  );
}

export default ServiceOrderDetails;
